import type { SVGProps } from 'react';

// One icon family for the app chrome: 24-unit grid, 2px round stroke,
// currentColor. Same construction as the BottomNav glyphs, so icons in
// cards, headers and empty states read as one set instead of a mix of
// emoji (platform-dependent, no stroke, no colour control) and SVG.
//
// Usage: <Icon.Bell size={20} /> — size defaults to 20px. Decorative by
// default (aria-hidden); pass `title` for a meaningful icon.

type IconProps = SVGProps<SVGSVGElement> & { size?: number; title?: string };

function base(paths: React.ReactNode) {
  return function IconComponent({ size = 20, title, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={title ? undefined : true}
        role={title ? 'img' : undefined}
        {...rest}
      >
        {title && <title>{title}</title>}
        {paths}
      </svg>
    );
  };
}

export const Icon = {
  ArrowLeft: base(
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </>
  ),
  ArrowRight: base(
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  ArrowDown: base(
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </>
  ),
  ArrowUp: base(
    <>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </>
  ),
  ChevronDown: base(<polyline points="6 9 12 15 18 9" />),
  Check: base(<polyline points="20 6 9 17 4 12" />),
  X: base(
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  Plus: base(
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  Clock: base(
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
  MessageCircle: base(
    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.8-.9L3 21l2-5.2A8.4 8.4 0 0 1 3 11.5 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
  ),
  Bell: base(
    <>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  Smartphone: base(
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </>
  ),
  Sparkles: base(
    <>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
      <path d="M19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8z" />
    </>
  ),
  Lightbulb: base(
    <>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.1 14.4A5.5 5.5 0 1 0 8.9 14.4c.6.6 1.1 1.3 1.1 2.1V17h4v-.5c0-.8.5-1.5 1.1-2.1z" />
    </>
  ),
  Sprout: base(
    <>
      <path d="M12 22V10" />
      <path d="M12 14c0-3.3-2.7-6-6-6H4v2c0 3.3 2.7 6 6 6h2" />
      <path d="M12 10c0-3.3 2.7-6 6-6h2v2c0 3.3-2.7 6-6 6h-2" />
    </>
  ),
  CloudSun: base(
    <>
      <path d="M12 2v2" />
      <path d="M4.9 4.9l1.4 1.4" />
      <path d="M2 12h2" />
      <path d="M19.1 4.9l-1.4 1.4" />
      <path d="M15.9 10.2A4 4 0 1 0 9 8.5" />
      <path d="M7 20h10a4 4 0 0 0 .6-7.9A5.5 5.5 0 0 0 7 14a3 3 0 0 0 0 6z" />
    </>
  ),
  Calendar: base(
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  Heart: base(
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
  ),
  HeartPulse: base(
    <>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
      <path d="M3.5 12h4l2-3 3 6 2-3h5" />
    </>
  ),
  Bowl: base(
    <>
      <path d="M4 12h16" />
      <path d="M4 12c0 4.4 3.6 8 8 8s8-3.6 8-8" />
      <path d="M9 5V4" />
      <path d="M12 5V3" />
      <path d="M15 5V4" />
    </>
  ),
  Utensils: base(
    <>
      <path d="M3 2v7a3 3 0 0 0 6 0V2" />
      <path d="M6 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6a2 2 0 0 0 2 2h3zm0 0v7" />
    </>
  ),
  Spoon: base(
    <>
      <path d="M14.5 2.5a4 4 0 0 1 0 6.3 4.2 4.2 0 0 1-4.6.6L4 15.3a1.8 1.8 0 0 0 2.6 2.6l5.9-5.9a4.2 4.2 0 0 1 .6-4.6 4 4 0 0 1 1.4-5z" />
    </>
  ),
  Leaf: base(
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.2 2 8 0 5.5-4.8 10-10 10z" />
      <path d="M2 21c0-3 1.9-5.5 4-6.5" />
    </>
  ),
  Salad: base(
    <>
      <path d="M7 21h10" />
      <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9z" />
      <path d="M11.4 12c-.2-2.6 1.4-5 3.3-6" />
      <path d="M6.6 12c.5-2 1.9-3.4 3.4-4" />
      <path d="M16 12c.1-1.8.8-3.2 2-4" />
    </>
  ),
  Coffee: base(
    <>
      <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
      <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <line x1="6" y1="2" x2="6" y2="4" />
      <line x1="10" y1="2" x2="10" y2="4" />
      <line x1="14" y1="2" x2="14" y2="4" />
    </>
  ),
  Camera: base(
    <>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </>
  ),
  Globe: base(
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  Settings: base(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  AlertTriangle: base(
    <>
      <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  Info: base(
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  ),
};

// Tinted square behind a card-header icon. Replaces the emoji-in-a-box
// pattern; the tint carries the category, the glyph carries the meaning.
export function IconTile({
  children,
  tone = 'primary',
  size = 'md',
  className = '',
}: {
  children: React.ReactNode;
  tone?: 'primary' | 'accent' | 'warning' | 'neutral';
  size?: 'sm' | 'md';
  className?: string;
}) {
  const toneClass = {
    primary: 'bg-primary-50 text-primary-700',
    accent: 'bg-accent-50 text-accent-600',
    warning: 'bg-warning/15 text-warning-700',
    neutral: 'bg-primary-50 text-slate-muted',
  }[tone];
  const sizeClass = size === 'sm' ? 'w-9 h-9 rounded-xl' : 'w-10 h-10 rounded-xl';
  return (
    <div className={`shrink-0 flex items-center justify-center ${sizeClass} ${toneClass} ${className}`} aria-hidden>
      {children}
    </div>
  );
}
